import { useEffect, useState, useCallback } from "react";
import { useAuth, useEtiquetaCritico } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard } from "@/components/common";
import { FotoImg } from "@/components/IAExplain";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, List, Plus, ArrowRight, CheckCircle2, TrendingUp, Clock } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { key: "abierta", label: "Abierta", color: "border-t-[#F59E0B]" },
  { key: "en_curso", label: "En curso", color: "border-t-[#3B82F6]" },
  { key: "resuelta", label: "Resuelta", color: "border-t-[#10B981]" },
  { key: "verificada", label: "Verificada", color: "border-t-[#0E2A47]" },
];
const origenLabel = { revision_libre: "Revisión libre", registro_plantilla: "Registro", manual: "Manual" };

export default function Incidencias() {
  const { user, scopeTenant } = useAuth();
  const krit = useEtiquetaCritico();
  const canEdit = ["superadmin", "admin", "calidad", "operario"].includes(user.rol);
  const canVerify = ["superadmin", "admin", "calidad"].includes(user.rol);
  const needsScope = useScopeGuard(user, scopeTenant);
  const [view, setView] = useState("kanban");
  const [incs, setIncs] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [filtros, setFiltros] = useState({ zona: "", vencidas: false });

  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [nueva, setNueva] = useState({ descripcion: "", zona: "", fecha_compromiso: "" });

  const [resOpen, setResOpen] = useState(false);
  const [resTarget, setResTarget] = useState(null);
  const [resDesc, setResDesc] = useState("");
  const [resFotos, setResFotos] = useState([]);

  const [escOpen, setEscOpen] = useState(false);
  const [escTarget, setEscTarget] = useState(null);
  const [esc, setEsc] = useState({ requisito: "", tipo: "mayor" });

  const load = useCallback(async () => {
    if (needsScope) return;
    const params = {};
    if (filtros.zona) params.zona = filtros.zona;
    if (filtros.vencidas) params.vencidas = true;
    const [i, r] = await Promise.all([
      api.get("/incidencias", { params }), api.get("/incidencias/resumen"),
    ]);
    setIncs(i.data); setResumen(r.data);
  }, [needsScope, filtros]);
  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    if (!nueva.descripcion) return toast.error("La descripción es obligatoria");
    try {
      await api.post("/incidencias", nueva);
      toast.success("Incidencia creada"); setNuevaOpen(false);
      setNueva({ descripcion: "", zona: "", fecha_compromiso: "" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const avanzar = async (inc, estado) => {
    try {
      await api.patch(`/incidencias/${inc.id}/estado`, { estado });
      toast.success(`Estado: ${estado}`); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const abrirResolver = (inc) => { setResTarget(inc); setResDesc(""); setResFotos([]); setResOpen(true); };
  const resolver = async () => {
    if (!resDesc) return toast.error("Describa la resolución");
    const fd = new FormData();
    fd.append("descripcion", resDesc);
    resFotos.slice(0, 5).forEach((f) => fd.append("fotos", f));
    try {
      await api.post(`/incidencias/${resTarget.id}/resolver`, fd);
      toast.success("Incidencia resuelta con evidencia"); setResOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const abrirEscalar = (inc) => { setEscTarget(inc); setEsc({ requisito: inc.requisito_id || "", tipo: "mayor" }); setEscOpen(true); };
  const escalar = async () => {
    try {
      await api.post(`/incidencias/${escTarget.id}/escalar`, esc);
      toast.success("Incidencia escalada a gap"); setEscOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  const cuadra = resumen?.cuadra;

  const renderCard = (inc) => (
    <Card key={inc.id} className="p-3 border-[#E2E8F0] mb-3" data-testid={`incidencia-${inc.id}`} data-vencida={inc.vencida}>
      <div className="flex items-start justify-between gap-2">
        <Badge variant="outline" className="text-[10px] border-[#E2E8F0]">{origenLabel[inc.origen] || inc.origen}</Badge>
        {inc.vencida && <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-[10px]"><Clock size={10} className="mr-1" />Vencida</Badge>}
      </div>
      <p className="text-sm text-[#0E2A47]/85 mt-2">{inc.descripcion}</p>
      {inc.fotos?.length > 0 && <div className="flex gap-1 mt-2">{inc.fotos.slice(0, 3).map((f) => <FotoImg key={f} id={f} className="w-12 h-12 object-cover rounded" />)}</div>}
      <div className="text-xs text-[#0E2A47]/50 mt-2">
        {inc.zona && <span>{inc.zona}</span>}
        {inc.fecha_compromiso && <span className={inc.vencida ? "text-[#EF4444] font-medium ml-2" : "ml-2"}>· {new Date(inc.fecha_compromiso).toLocaleDateString("es-ES")}</span>}
      </div>
      {canEdit && (
        <div className="flex flex-wrap gap-1 mt-3">
          {inc.estado === "abierta" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => avanzar(inc, "en_curso")} data-testid={`inc-empezar-${inc.id}`}><ArrowRight size={12} className="mr-1" />Empezar</Button>}
          {inc.estado === "en_curso" && <Button size="sm" className="h-7 text-xs bg-[#10B981] hover:bg-[#0f9d74]" onClick={() => abrirResolver(inc)} data-testid={`inc-resolver-${inc.id}`}>Resolver</Button>}
          {inc.estado === "resuelta" && canVerify && <Button size="sm" className="h-7 text-xs bg-[#0E2A47] hover:bg-[#163C65]" onClick={() => avanzar(inc, "verificada")} data-testid={`inc-verificar-${inc.id}`}><CheckCircle2 size={12} className="mr-1" />Verificar</Button>}
          {["abierta", "en_curso"].includes(inc.estado) && !inc.gap_id && <Button size="sm" variant="ghost" className="h-7 text-xs text-[#FF5625]" onClick={() => abrirEscalar(inc)} data-testid={`inc-escalar-${inc.id}`}><TrendingUp size={12} className="mr-1" />Escalar</Button>}
          {inc.gap_id && <Badge variant="outline" className="text-[10px] border-[#FF5625] text-[#FF5625]">Escalada</Badge>}
        </div>
      )}
    </Card>
  );

  return (
    <div data-testid="incidencias-page" data-verify-incidencias-cuadra={cuadra}>
      <PageHeader overline="Nivel 2 · Registra" title="Incidencias" help="incidencias"
        action={
          <div className="flex gap-2 items-center">
            <div className="flex bg-white border border-[#E2E8F0] rounded-md">
              <button onClick={() => setView("kanban")} data-testid="view-kanban" className={`px-3 py-2 ${view === "kanban" ? "bg-[#0E2A47] text-white" : "text-[#0E2A47]/60"}`}><LayoutGrid size={16} /></button>
              <button onClick={() => setView("lista")} data-testid="view-lista" className={`px-3 py-2 ${view === "lista" ? "bg-[#0E2A47] text-white" : "text-[#0E2A47]/60"}`}><List size={16} /></button>
            </div>
            {canEdit && (
              <Dialog open={nuevaOpen} onOpenChange={setNuevaOpen}>
                <DialogTrigger asChild><Button data-testid="nueva-incidencia-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={16} className="mr-1" />Incidencia</Button></DialogTrigger>
                <DialogContent className="bg-white" data-testid="incidencia-dialog">
                  <DialogHeader><DialogTitle className="text-[#0E2A47]">Nueva incidencia</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Descripción</Label>
                      <Textarea value={nueva.descripcion} onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })} data-testid="inc-descripcion" className="mt-1.5" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Zona</Label>
                        <Input value={nueva.zona} onChange={(e) => setNueva({ ...nueva, zona: e.target.value })} data-testid="inc-zona" className="mt-1.5" /></div>
                      <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Fecha compromiso</Label>
                        <Input type="date" value={nueva.fecha_compromiso} onChange={(e) => setNueva({ ...nueva, fecha_compromiso: e.target.value })} data-testid="inc-fecha" className="mt-1.5" /></div>
                    </div>
                  </div>
                  <DialogFooter><Button onClick={crear} data-testid="inc-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Crear</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        } />

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <Input placeholder="Filtrar por zona" value={filtros.zona} onChange={(e) => setFiltros({ ...filtros, zona: e.target.value })} data-testid="filtro-zona" className="max-w-xs" />
        <label className="flex items-center gap-2 text-sm text-[#0E2A47]"><input type="checkbox" checked={filtros.vencidas} onChange={(e) => setFiltros({ ...filtros, vencidas: e.target.checked })} data-testid="filtro-vencidas" /> Solo vencidas</label>
        {resumen && <span className="text-xs text-[#0E2A47]/50 ml-auto" data-testid="incidencias-cuadra">Total {resumen.total} · balance {cuadra ? "✓ cuadra" : "✗"}</span>}
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {COLS.map((col) => (
            <div key={col.key} data-testid={`col-${col.key}`}>
              <div className={`bg-white border border-[#E2E8F0] border-t-2 ${col.color} rounded-t-md px-3 py-2 flex items-center justify-between`}>
                <span className="font-semibold text-sm text-[#0E2A47]">{col.label}</span>
                <span className="text-xs text-[#0E2A47]/40">{incs.filter((i) => i.estado === col.key).length}</span>
              </div>
              <div className="p-2 bg-[#F7F8FA] rounded-b-md min-h-[200px]">
                {incs.filter((i) => i.estado === col.key).map(renderCard)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{incs.map(renderCard)}</div>
      )}

      {/* Resolver */}
      <Dialog open={resOpen} onOpenChange={setResOpen}>
        <DialogContent className="bg-white" data-testid="resolver-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Resolver incidencia</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Descripción de la resolución</Label>
              <Textarea value={resDesc} onChange={(e) => setResDesc(e.target.value)} data-testid="res-descripcion" className="mt-1.5" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Fotos de evidencia</Label>
              <Input type="file" accept="image/*" multiple onChange={(e) => setResFotos(Array.from(e.target.files))} data-testid="res-fotos" className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={resolver} data-testid="res-submit" className="bg-[#10B981] hover:bg-[#0f9d74]">Marcar resuelta</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalar */}
      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent className="bg-white" data-testid="escalar-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Escalar a gap</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Requisito</Label>
              <Input value={esc.requisito} onChange={(e) => setEsc({ ...esc, requisito: e.target.value })} data-testid="esc-requisito" className="mt-1.5" placeholder="4.12.1" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Tipo</Label>
              <Select value={esc.tipo} onValueChange={(v) => setEsc({ ...esc, tipo: v })}>
                <SelectTrigger data-testid="esc-tipo" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="KO">{krit}</SelectItem><SelectItem value="mayor">Mayor</SelectItem><SelectItem value="menor">Menor</SelectItem></SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={escalar} data-testid="esc-submit" className="bg-[#FF5625] hover:bg-[#E0451A]">Escalar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
